import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "hiddenId", "dropdown", "createLink", "phone", "email", "icNo"]
  static values = { searchUrl: String }

  connect() {
    this._debounceTimer = null
    this._outsideClickHandler = this._handleOutsideClick.bind(this)
    this._selectHandler = (e) => this._selectCustomer(e.detail)
    this._submitHandler = this._validateOnSubmit.bind(this)
    document.addEventListener("click", this._outsideClickHandler)
    this.element.addEventListener("customer-search:select", this._selectHandler)
    this.element.closest("form").addEventListener("submit", this._submitHandler)
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClickHandler)
    this.element.removeEventListener("customer-search:select", this._selectHandler)
    const form = this.element.closest("form")
    if (form) form.removeEventListener("submit", this._submitHandler)
  }

  search() {
    // If admin clears or changes the input after selecting, reset customer_id
    if (!this.inputTarget.value.trim()) {
      this.hiddenIdTarget.value = ""
    } else if (this.hiddenIdTarget.value) {
      // Name changed after selection — invalidate the selection
      this.hiddenIdTarget.value = ""
    }
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._doSearch(), 300)
  }

  _doSearch() {
    const q = this.inputTarget.value.trim()
    if (q.length < 1) {
      this._hideDropdown()
      return
    }

    fetch(`${this.searchUrlValue}?q=${encodeURIComponent(q)}`, {
      headers: { "Accept": "application/json" }
    })
      .then(r => r.json())
      .then(customers => this._renderDropdown(customers, q))
  }

  _renderDropdown(customers, q) {
    this.dropdownTarget.innerHTML = ""

    if (customers.length === 0) {
      const noResult = document.createElement("div")
      noResult.className = "customer-search-empty px-3 py-2 text-muted small"
      noResult.textContent = "No customers found."
      this.dropdownTarget.appendChild(noResult)
    } else {
      customers.forEach(c => {
        const item = document.createElement("button")
        item.type = "button"
        item.className = "customer-search-item list-group-item list-group-item-action px-3 py-2"
        item.innerHTML = `<span class="badge bg-secondary me-2">${c.customer_code}</span><strong>${c.name}</strong><span class="text-muted small ms-2">${c.email || ""}</span>`
        item.addEventListener("click", () => this._selectCustomer(c))
        this.dropdownTarget.appendChild(item)
      })
    }

    // Always show "Create customer" link
    const divider = document.createElement("div")
    divider.className = "dropdown-divider"
    this.dropdownTarget.appendChild(divider)

    const createBtn = document.createElement("button")
    createBtn.type = "button"
    createBtn.className = "customer-search-create list-group-item list-group-item-action px-3 py-2 text-primary"
    createBtn.innerHTML = `<i class="bi bi-plus-circle me-1"></i>Create new customer`
    createBtn.addEventListener("click", () => this._openCreateModal(q))
    this.dropdownTarget.appendChild(createBtn)

    this.dropdownTarget.classList.remove("d-none")
  }

  _selectCustomer(c) {
    this.inputTarget.value = c.name
    this.hiddenIdTarget.value = c.id

    if (this.hasPhoneTarget) this.phoneTarget.value = c.phone || ""
    if (this.hasEmailTarget) this.emailTarget.value = c.email || ""
    if (this.hasIcNoTarget) this.icNoTarget.value = c.ic_no || ""

    this._hideDropdown()
  }

  _openCreateModal(prefill) {
    this._hideDropdown()
    const modal = document.getElementById("createCustomerModal")
    if (modal) {
      const nameInput = modal.querySelector("#modal_customer_name")
      if (nameInput) nameInput.value = prefill
      const bsModal = new window.bootstrap.Modal(modal)
      bsModal.show()
    }
  }

  _validateOnSubmit(event) {
    const errorEl = document.getElementById("customerRequiredError")
    const inputEl = this.inputTarget

    if (!this.hiddenIdTarget.value) {
      event.preventDefault()
      inputEl.classList.add("is-invalid")
      if (errorEl) errorEl.style.display = "block"
      inputEl.scrollIntoView({ behavior: "smooth", block: "center" })
      inputEl.focus()
    } else {
      inputEl.classList.remove("is-invalid")
      if (errorEl) errorEl.style.display = "none"
    }
  }

  _hideDropdown() {
    this.dropdownTarget.classList.add("d-none")
    this.dropdownTarget.innerHTML = ""
  }

  _handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this._hideDropdown()
    }
  }

  // Called by the create-customer modal after successful creation
  selectCreated(customer) {
    this._selectCustomer(customer)
  }
}
